import React, { useState, useEffect } from "react";
import {
  initialNodeStyle,
  initialDirectedEdgeStyle,
  visitedNodeStyle,
  visitedDirectedEdgeStyle,
  visitingNodeStyle,
  visitingDirectedWeightedEdgeStyle,
} from "./GraphStyle";

const BellmanFord = ({ cytoscape, nodes, startingNode }) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [distances, setDistances] = useState([]);
  const [parentNodes, setParentNodes] = useState([]);

  const [buttonLabel, setButtonLabel] = useState("Set Initial Distances");
  const [isBellmanFordCompleted, setisBellmanFordCompleted] = useState(false);
  const [isSetInitialsCompleted, setInitialsCompleted] = useState(false);
  const [rounds, setRounds] = useState(0);
  const [noUpdatesMessage, setNoUpdatesMessage] = useState("");

  const handleButtonClick = async () => {
    if (isAnimating) return;
    setIsAnimating(true);

    if (buttonLabel === "Set Initial Distances") {
      setInitials(cytoscape);
      setButtonLabel("Perform Bellman Ford Algorithm");
    } else if (buttonLabel === "Perform Bellman Ford Algorithm") {
      performBF(cytoscape);
      setButtonLabel("Reset Animation");
      //setisBellmanFordCompleted(false);
    } else if (buttonLabel === "Reset Animation") {
      resetAnimation();
      setButtonLabel("Set Initial Distances");
    }
    setIsAnimating(false);
  };

  const resetAnimation = () => {
    // Reset styles of all nodes and edges
    cytoscape.elements("node").style(initialNodeStyle);
    cytoscape.elements("edge").style(initialDirectedEdgeStyle);

    // Reset all state variables
    setDistances([]);
    setParentNodes([]);
    setButtonLabel("Set Initial Distances");
    setisBellmanFordCompleted(false);
    setInitialsCompleted(false);
    setRounds(0);
    setNoUpdatesMessage("");

    setIsAnimating(false);
  };

  const setInitials = () => {
    const distancesArray = {};
    const parentNodesArray = {};
    for (const node of nodes) {
      distancesArray[node.data.id] = Infinity;
      parentNodesArray[node.data.id] = null;
    }
    distancesArray["node" + startingNode] = 0;
    setRounds(nodes.length - 1);
    setDistances([{ round: 0, data: { ...distancesArray } }]);
    setParentNodes([{ round: 0, data: { ...parentNodesArray } }]);
    setInitialsCompleted(true);
    setIsAnimating(false);
  };

  const performBF = () => {
    const vertices = nodes.length;

    // Initialize parent nodes array with initial values
    const initialParentNodes = {};
    nodes.forEach((node) => {
      initialParentNodes[node.data.id] = null;
    });

    // Initialize distances array with initial values
    const initialDistances = {};
    nodes.forEach((node) => {
      initialDistances[node.data.id] = Infinity;
    });
    initialDistances["node" + startingNode] = 0;

    // Accumulate changes to parent nodes and distances
    const newParentNodesArray = [{ round: 0, data: { ...initialParentNodes } }];
    const newDistancesArray = [{ round: 0, data: { ...initialDistances } }];

    // Flag to track if any distance is updated in the current round
    let isDistanceUpdated = false;
    let noUpdates = false;
    let roundBreak = 0;
    for (let i = 1; i < vertices; i++) {
      // Reset the flag for each round
      isDistanceUpdated = false;
      roundBreak = i;
      // Update distances and parent nodes for the current round
      const prevDistances = newDistancesArray[i - 1].data;
      const newDistances = { round: i, data: { ...prevDistances } };
      const newParentNodes = {
        round: i,
        data: { ...newParentNodesArray[i - 1].data },
      };

      // Sort edges in descending order of weights
      const sortedEdges = cytoscape.edges().sort((a, b) => {
        return b.data("weight") - a.data("weight");
      });

      sortedEdges.forEach((edge) => {
        const sourceNodeId = edge.source().data("id");
        const targetNodeId = edge.target().data("id");
        const edgeWeight = edge.data("weight");
        // Skip updating if the target node is the starting node
        const targetNodeIdInteger = parseInt(targetNodeId.replace("node", ""));

        // Skip updating if the target node is the starting node
        if (targetNodeIdInteger !== startingNode) {
          const newDistance =
            Number(newDistances.data[sourceNodeId]) + Number(edgeWeight);

          // Update the distance and parent nodes if a shorter path is found
          if (newDistance < newDistances.data[targetNodeId]) {
            newDistances.data[targetNodeId] = newDistance;
            newParentNodes.data[targetNodeId] = sourceNodeId;

            // Set the flag to true if any distance is updated
            isDistanceUpdated = true;
          }
        }
      });

      // If no distance is updated in the current round, exit the loop
      if (!isDistanceUpdated) {
        noUpdates = true;
        // Mark Bellman-Ford as completed
        setisBellmanFordCompleted((prevState) => true); // Correct when previous state is needed

        break;
      }
      // Push the updated distances and parent nodes into the arrays
      newDistancesArray.push(newDistances);
      newParentNodesArray.push(newParentNodes);
    }

    // Update the distances and parentNodes states after the loop
    setDistances(newDistancesArray);
    setParentNodes(newParentNodesArray);

    if (noUpdates) {
      setisBellmanFordCompleted((prevState) => true);

      setNoUpdatesMessage(
        `No distances were updated after round - ${
          roundBreak - 1
        }, because no edge was used for relaxing distances.`
      );
    } else {
      setisBellmanFordCompleted((prevState) => true);
      setNoUpdatesMessage("");
    }
  };

  useEffect(() => {
    // Perform animation when Bellman-Ford is completed
    if (isBellmanFordCompleted) {
      animateFinalPath();
    }
  }, [isBellmanFordCompleted]);

  const animateFinalPath = async () => {
    let parentNodesLength = parentNodes.length;
    const startingNodeId = "node" + startingNode;
    await new Promise((resolve) => setTimeout(resolve, 1000));
    const startNode = nodes.find((node) => node.data.id === startingNodeId);
    cytoscape.nodes(`[id='${startNode.data.id}']`).style({
      ...visitedNodeStyle,
    });
    await new Promise((resolve) => setTimeout(resolve, 1000));
    const finalRoundParent = parentNodes[parentNodesLength - 1]?.data;
    for (let i = 1; i <= nodes.length; i++) {
      const currentNodeId = "node" + i;
      const currentNode = nodes.find((node) => node.data.id === currentNodeId);

      if (finalRoundParent[currentNodeId] != null) {
        cytoscape.nodes(`[id='${currentNode.data.id}']`).style({
          ...visitedNodeStyle,
        });
        await new Promise((resolve) => setTimeout(resolve, 1000));
        const sourceId = finalRoundParent[currentNodeId];
        const edgeId = `[source="${sourceId}"][target="${currentNodeId}"]`;
        const edge = cytoscape.edges(edgeId);
        edge.style({
          ...visitedDirectedEdgeStyle,
        });
      }
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
  };

  return (
    <div className="topological-sort-container">
      <div className="topological-sort-buttons" style={{ marginTop: "2%" }}>
        <button
          className="generate-reset-button"
          onClick={handleButtonClick}
          disabled={
            isAnimating ||
            (!isSetInitialsCompleted &&
              buttonLabel === "Perform Bellman Ford Algorithm") ||
            (!isBellmanFordCompleted && buttonLabel === "Reset Animation")
          }
        >
          {isAnimating ? "Animating..." : buttonLabel}
        </button>
        &nbsp;&nbsp;
        {/*<button
          className="generate-reset-button"
          onClick={resetAnimation}
          disabled={isAnimating}
        >
          Reset Animation
            </button>*/}
      </div>
      {/*<div>
        <div className="stack-container">
          <div className="stack-title">Stack:</div> &nbsp;
          <ul className="stack">
            {[...stack].reverse().map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>
      </div>
      <div>
        <div className="stack-container">
          <div className="stack-title">Topological Sort Order:</div>
          <ul className="stack">
            {[...allVisitedNodes].reverse().map((visitedNode, index) => (
              <li key={index}>{visitedNode}</li>
            ))}
          </ul>
        </div>
      </div>*/}
      {isBellmanFordCompleted && (
        <div className="table-container" style={{ marginTop: "2%" }}>
          <table className="custom-table">
            <tbody>
              <tr>
                <th></th>
                {[...Array(rounds + 1).keys()].map((roundNumber) => (
                  <th key={`round-${roundNumber}`}>Round {roundNumber}</th>
                ))}
              </tr>
              {Object.keys(distances[0]?.data || {}).map((nodeId) => (
                <tr key={`node-${nodeId}`}>
                  <th>{nodeId}</th>
                  {distances.map((roundData, index) => (
                    <React.Fragment key={`round-${index}`}>
                      <td>
                        {roundData && roundData.data[nodeId]} (
                        {parentNodes[index]?.data[nodeId] !== null
                          ? parentNodes[index]?.data[nodeId]
                          : "null"}
                        )
                      </td>
                    </React.Fragment>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {noUpdatesMessage && (
            <div className="no-updates-message" style={{ marginTop: "2%" }}>
              {noUpdatesMessage}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BellmanFord;
